/**
 * Constants for player-kanban feature
 */

// Feature-specific enum constants
export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

// Pagination constants
/** Number of kanban cards fetched per page — shared across all useListKanbanCardsQuery call sites so Apollo cache keys are identical */
export const CARDS_PAGE_SIZE = 40;

/**
 * SE-6796: page size for the fallback board lookup in `useKanbanBoardQueries`.
 *
 * This read answers two questions — "does this account have any board?" and
 * "which board should the screen land on?" — and it used to ask for a single
 * row. One row is too little for the second question: in an org with many
 * boards the intended WATCHLIST board need not be the newest, so the screen
 * resolved an arbitrary board and, when the window missed, concluded no board
 * existed at all.
 *
 * 40 deliberately matches `useBoardManager`'s page size so both call sites
 * collapse into ONE normalized `ListMyKanbanBoards` cache entry (they mount on
 * the same screen) instead of fragmenting into two, and because 40 is the
 * largest page size this query is proven to serve within the backend's
 * complexity budget.
 */
export const BOARDS_LOOKUP_PAGE_SIZE = 40;

// UI constants
export const UI_CONSTANTS = {
  DEFAULT_PAGE_SIZE: 20,
  DEBOUNCE_TIMEOUT: 300, // milliseconds
};

// Message constants
export const MESSAGES = {
  SUCCESS: {
    CREATED: "Item created successfully",
    UPDATED: "Item updated successfully",
    DELETED: "Item deleted successfully",
  },
  ERROR: {
    NOT_FOUND: "Item not found",
    UNAUTHORIZED: "Unauthorized access",
    GENERIC: "An error occurred. Please try again.",
    ADD_PLAYER_FAILED: "Failed to add player. Please try again.",
    /**
     * SE-7120: shown when a `?boardId=` deep link (a Pipeline update tapped in
     * Home > Updates) names a board this viewer's resolved board list does not
     * contain. The screen still falls back to the default board — that part is
     * fine — but saying nothing made the landing indistinguishable from a
     * broken link, because the fallback board is real, populated, and has
     * nothing to do with the update that was clicked.
     *
     * Names both plausible causes, since the client cannot tell them apart: a
     * board the viewer's role cannot see, and one that no longer exists.
     */
    BOARD_DEEP_LINK_UNRESOLVED:
      "Couldn't open that pipeline. It may have been deleted, or it may not be available to your role.",
    /**
     * SE-6270: shown when a pipeline picked from the chip row or the "See all
     * pipelines" drawer is not in the board manager's SETTLED, COMPLETE board
     * list. The pick previously did nothing at all - no board change, no error,
     * no toast - which a user cannot tell apart from a mis-click or a slow app.
     *
     * Deliberately worded for a board that is genuinely gone or invisible to
     * this viewer. A board we merely failed to finish loading gets
     * BOARD_SELECTION_INCOMPLETE instead, because "we cannot see it" is not
     * evidence that it was deleted.
     */
    BOARD_SELECTION_UNRESOLVED:
      "Couldn't switch to that pipeline. It may have been deleted, or it may not be available to your role.",
    /**
     * SE-6270: shown when the pick missed AND the board list is known to be
     * incomplete (the connection walk hit its page cap, a page request failed,
     * or the first page never landed). The pipeline probably still exists - we
     * just never received it - so this asks for a retry rather than asserting
     * the board is gone.
     */
    BOARD_SELECTION_INCOMPLETE:
      "Couldn't switch to that pipeline - the pipeline list didn't finish loading. Please refresh and try again.",
  },
  EMPTY: {
    LIST: "No items found",
    SEARCH: "No results match your search",
    FILTER: "No results match your filters",
  },
};

// =============================================================================
// PIPELINE REVIEW QUEUE (SE-5499)
// =============================================================================

/**
 * Reason auto-supplied on every Review-queue acceptance. The
 * `AcceptPipelineReviewRequestInput.reason` field is `String!` (required, and
 * rejected server-side when blank after trim), but the product decision (Pietra,
 * 2026-07-16) is that accepting into a sensitive/terminal stage moves the player
 * SILENTLY — there is NO reason prompt on accept. We satisfy the required field
 * with this constant so the UI never has to ask the manager for a reason.
 */
export const PIPELINE_REVIEW_ACCEPT_REASON = "Accepted from Review queue";

/** Page size for each per-status pipelineReviewQueue query. */
export const REVIEW_QUEUE_PAGE_SIZE = 50;

// =============================================================================
// PIPELINE AUTOMATION RULES (SE-5506)
// =============================================================================

/** Page size for the per-pipeline automation-rules list query. */
export const AUTOMATION_RULES_PAGE_SIZE = 100;

// =============================================================================
// PIPELINE ACTIVITY & AUDIT TRAIL (SE-5504)
// =============================================================================

/**
 * Page size for `pipelineActivityLog`, shared by the per-card trail and the
 * per-pipeline log so both collapse into ONE normalized cache entry per scope
 * rather than fragmenting on `$first` (SE-5058).
 */
export const ACTIVITY_TRAIL_PAGE_SIZE = 25;

/**
 * Page size for the org-roster read that resolves `actorUserId` to a name.
 * Matches the value `BoardShareSheetContainer` and `useAutoShareWithManagers`
 * already use, so the three share one cache entry instead of three.
 */
export const ORG_USERS_PAGE_SIZE = 500;

// =============================================================================
// PIPELINE DRAWER GEOMETRY (SE-6554)
// =============================================================================

/**
 * The desktop width shared by the board's three READ drawers — Player preview,
 * Review queue and Activity log.
 *
 * The `Drawer` atom sizes itself as a FRACTION of the viewport (`sm` = `w-1/4`,
 * `md` = `w-1/2`, `lg` = `w-3/4`), so `size="md"` is not a width at all: on a
 * 2560px monitor it is a 1280px panel. That is why all three read as "too
 * wide" — they were never given a width, only a share of the screen. Add
 * Players already escapes the same trap with its own `!w-90`.
 *
 * `w-96` (384px) is the sizing ladder's nearest rung to the three artboards
 * (Player preview 393, Activity log 393, Review queue 398) and lands within
 * 14px of all of them. One rung is used for all three rather than a per-drawer
 * value: the artboards differ by 5px, which is measurement noise in the design
 * file, and three drawers that open from the same toolbar sliding to three
 * different widths is a defect the specs do not actually ask for.
 *
 * NOT applied on small screens — there the drawers anchor `bottom` as sheets
 * and must span the viewport, so the sheet class carries padding only.
 *
 * Written as whole literal class strings (never composed) so the Tailwind
 * scanner can see them; `!` because the atom's fractional width is a tva class
 * that a plain `w-96` does not beat.
 */
export const PIPELINE_DRAWER_CONTENT_CLASS = "!w-96 !p-0";

/** The small-screen (bottom-sheet) counterpart — padding only, full width. */
export const PIPELINE_DRAWER_SHEET_CLASS = "!p-0";

// Export all constants as a namespace
export const Constants = {
  SortOrder,
  UI_CONSTANTS,
  MESSAGES,
};
